import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronLeft, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { BrandLogo } from "../components/BrandLogo";
import StepIndicator from "../components/wizard/StepIndicator";
import KitStep from "../components/wizard/KitStep";
import AddressStep from "../components/wizard/AddressStep";
import ExtrasStep from "../components/wizard/ExtrasStep";
import PaymentStep from "../components/wizard/PaymentStep";
import { api } from "../lib/api";
import { useT } from "../lib/i18n";
import { loadDraft, saveDraft } from "../lib/wizardStore";
import { track, getSessionId } from "../lib/track";
import { toast } from "sonner";

export default function Wizard() {
  const { t } = useT();
  const navigate = useNavigate();
  const loc = useLocation();
  const [settings, setSettings] = useState(null);
  const [draft, setDraft] = useState(loadDraft());
  const [step, setStep] = useState(0);
  const [paying, setPaying] = useState(false);

  const extrasEnabled = (settings?.add_ons || []).length > 0;
  // Step keys drive a dynamic flow: the Extras step only appears when add-ons exist.
  const stepKeys = useMemo(
    () => (extrasEnabled ? ["kit", "address", "extras", "payment"] : ["kit", "address", "payment"]),
    [extrasEnabled]
  );
  const labelFor = { kit: t("step_kit"), address: t("step_contact"), extras: t("extras_step"), payment: t("step_payment") };
  const steps = stepKeys.map((k) => labelFor[k]);
  const currentKey = stepKeys[step];
  const lastIndex = stepKeys.length - 1;

  useEffect(() => {
    api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    const params = new URLSearchParams(loc.search);
    const kitParam = params.get("kit");
    if (kitParam) { const q = parseInt(kitParam, 10); setDraft((d) => (d.kit_qty ? d : { ...d, kit_qty: q })); }
    const src = params.get("src");
    if (src) setDraft((d) => ({ ...d, source: src }));
  }, [loc.search]);
  useEffect(() => { saveDraft(draft); }, [draft]);
  // Clamp the step index if the flow length changes (e.g. gift toggled off).
  useEffect(() => { if (step > lastIndex) setStep(lastIndex); }, [lastIndex, step]);
  useEffect(() => { track("flow_started", { source: draft.source || "" }); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (draft.kit_qty) track("quantity_selected", { meta: { kit_qty: draft.kit_qty } }); }, [draft.kit_qty]);
  useEffect(() => {
    if (currentKey === "extras") track("extras_reached");
    if (currentKey === "payment") track("payment_reached");
  }, [currentKey]);

  const emailValid = /.+@.+\..+/.test(draft.customer.email || "");
  const addressValid = draft.customer.name.trim() && emailValid && draft.address.line1.trim() &&
    draft.address.city.trim() && draft.address.state.trim() && draft.address.postal_code.trim();

  const canContinue = useMemo(() => {
    if (currentKey === "kit") return !!draft.kit_qty;
    if (currentKey === "address") return !!addressValid;
    return true;
  }, [currentKey, draft, addressValid]);

  const next = () => {
    if (!canContinue) {
      if (currentKey === "address") toast.error("Please complete the required fields.");
      return;
    }
    if (currentKey === "address") {
      track("contact_completed", { source: draft.source || "", meta: {
        name: draft.customer.name, email: draft.customer.email, phone: draft.customer.phone || "",
        kit_qty: draft.kit_qty,
      }});
    }
    if (step < lastIndex) setStep(step + 1);
  };
  const back = () => { if (step === 0) navigate("/"); else setStep(step - 1); };

  const pay = async (method) => {
    setPaying(true);
    track("payment_initiated", { meta: { method } });
    try {
      const payload = {
        kit_qty: draft.kit_qty,
        customer: { name: draft.customer.name, email: draft.customer.email, phone: draft.customer.phone || "" },
        address: draft.address,
        add_ons: extrasEnabled
          ? Object.entries(draft.add_ons || {}).map(([id, qty]) => ({ id, qty: Number(qty) || 0 })).filter((x) => x.qty > 0)
          : [],
        payment_method: method,
        origin_url: window.location.origin,
        session_id: getSessionId(),
        discount: draft.discount && draft.discount.type === "first_order"
          ? { type: "first_order", email: draft.discount.email }
          : null,
      };
      const r = await api.post("/orders/checkout", payload);
      if (r.data.mode === "stripe") {
        window.location.href = r.data.checkout_url;
      } else {
        navigate(`/order/success?order=${encodeURIComponent(r.data.order_number)}&zelle=1`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start payment.");
      setPaying(false);
    }
  };

  return (
    <div className="min-h-dvh pb-28">
      <header className="sticky top-0 z-40 border-b bg-[hsl(var(--background))]/90 backdrop-blur">
        <div className="mx-auto flex max-w-xl items-center gap-3 px-4 py-3">
          <button onClick={back} data-testid="wizard-back-button" className="grid h-9 w-9 place-items-center rounded-full border bg-white"><ChevronLeft className="h-5 w-5" /></button>
          <BrandLogo size={34} />
        </div>
      </header>

      <div className="mx-auto max-w-xl px-4 py-5">
        <StepIndicator steps={steps} current={step} />
        <h1 className="mt-4 font-display text-2xl font-semibold" style={{ color: "hsl(var(--brand-brown))" }}>{steps[step]}</h1>
        <div className="mt-4">
          {!settings ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : (
            <>
              {currentKey === "kit" && <KitStep draft={draft} setDraft={setDraft} settings={settings} />}
              {currentKey === "address" && <AddressStep draft={draft} setDraft={setDraft} settings={settings} addressValid={!!addressValid} />}
              {currentKey === "extras" && <ExtrasStep draft={draft} setDraft={setDraft} settings={settings} />}
              {currentKey === "payment" && <PaymentStep draft={draft} settings={settings} onPay={pay} paying={paying} zelleEnabled={settings?.payments?.zelle_enabled} />}
            </>
          )}
        </div>
      </div>

      <div className="fixed inset-x-0 bottom-0 z-40 border-t bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-xl items-center gap-3 px-4 py-3">
          <Button variant="outline" onClick={back} className="h-12 rounded-xl border bg-white px-5">{t("back")}</Button>
          {step < lastIndex && (
            <Button onClick={next} disabled={!canContinue} data-testid="wizard-continue-button" className="h-12 flex-1 rounded-xl text-base">{t("continue")}</Button>
          )}
        </div>
      </div>
    </div>
  );
}
